import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth-store';
import { useCategoryStore } from '../../stores/category-store';
import { useTagStore } from '../../stores/tag-store';
import { useUserStore } from '../../stores/user-store';
import { canAccessAdminPanel } from '../../utils/permissions';
import { isFaqSpaceLikePath } from '../../faq/faq-space-utils';

interface AuthGuardProps {
  children: React.ReactNode;
}

// 认证页面：登录后应重定向离开
const authRoutes = ['/login', '/register'];
// 公开内容页面：登录/未登录都可访问，登录后不重定向
const contentRoutes = ['/portal'];
// 所有公开路由
const publicRoutes = [...authRoutes, ...contentRoutes];

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { user, loading, checkAuth } = useAuthStore();
  const { fetchCategories } = useCategoryStore();
  const { fetchTags } = useTagStore();
  const { profile } = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [authChecked, setAuthChecked] = React.useState(false);

  useEffect(() => {
    const performAuthCheck = async () => {
      try {
        await checkAuth();
      } catch (error) {
        console.error('认证检查失败:', error);
      } finally {
        setAuthChecked(true);
      }
    };

    performAuthCheck();
  }, [checkAuth]);

  useEffect(() => {
    // 只在认证检查完成且不在加载状态时进行路由判断
    if (authChecked && !loading) {
      const isPortalRoute = location.pathname.startsWith('/portal');
      const isFaqRoute = isFaqSpaceLikePath(location.pathname);
      const isPublicRoute = isFaqRoute || publicRoutes.some(route => location.pathname.startsWith(route));
      const isAuthPage = authRoutes.some(route => location.pathname.includes(route));
      const isAdminRoute = location.pathname.startsWith('/uadmin');

      console.log('AuthGuard 路由检查:', {
        pathname: location.pathname,
        user: !!user,
        userRole: profile?.role,
        loading,
        authChecked,
        isPortalRoute,
        isFaqRoute,
        isPublicRoute,
        isAuthPage,
        isAdminRoute
      });

      // 如果用户未登录且不在公共路由上，重定向到登录页
      if (!user && !isPublicRoute) {
        console.log('用户未登录，重定向到登录页, 来源:', location.pathname);
        navigate('/cn/login', { state: { from: location.pathname } });
        return;
      }

      // 如果用户已登录但试图访问管理面板且权限不足，重定向到公共门户
      if (user && profile && isAdminRoute && !canAccessAdminPanel(profile.role)) {
        console.log('用户权限不足，无法访问管理面板，重定向到公共门户');
        navigate('/portal');
        return;
      }

      // 如果用户已登录且访问登录/注册页面，重定向（不影响 /faq、/portal 等内容页）
      if (user && profile && isAuthPage) {
        console.log('用户已登录，从认证页面重定向');
        // 根据用户权限决定重定向目标
        if (canAccessAdminPanel(profile.role)) {
          navigate('/uadmin/');
        } else {
          navigate('/portal');
        }
        return;
      }

      // 当用户认证成功后，预加载分类和标签数据
      if (user && profile) {
        // 并行预加载分类和标签
        Promise.all([
          fetchCategories(),
          fetchTags()
        ]).catch(error => {
          console.error('预加载数据失败:', error);
        });
      }
    }
  }, [user, profile, loading, authChecked, navigate, location.pathname, fetchCategories]);

  if (loading || !authChecked) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return <>{children}</>;
}

export default AuthGuard;
